import React, { useEffect, useState } from "react";
import { AddSupplyUnitWizard } from "./AddSupplyUnitWizard/AddSupplyUnitWizard";
import {
  Table,
  Grid,
  Header,
  Button,
  Divider,
  Segment,
  Modal,
  Icon,
  Loader,
  Dimmer,
  Label,
  Form,
} from "semantic-ui-react";
import { HierarchicalSupplyUnitsList } from "./HierchichalSupplyUnitDisplay";
import axios from "../../../apis/dvdlocator";
import { isValidChildPartNumber } from "../../../validation/validateSupplyPartNumber";
import { validateProductPartNumber } from "../../../validation/validateProductPartNumber";
import type { WizardSupplyUnit, ProductData } from "./AddSupplyUnitWizard/types";

interface AddSupplyUnitPageProps {
  product: ProductData;
  setProduct: (product: ProductData) => void;
  nextPage?: () => void;
  prevPage?: () => void;
  setSupplyUnits: React.Dispatch<React.SetStateAction<WizardSupplyUnit[]>>;
  supplyUnits: WizardSupplyUnit[];
  initialProduct?: ProductData | null;
  preselectedSupplyUnits?: WizardSupplyUnit[];
  mode?: "create" | "duplicate" | "edit";
  /**
   * For use inside another form (Create Related Listing). Hides the product
   * summary table and the Back/Review footer, and reports every Part Number
   * change through setProduct so the parent always has the current value.
   */
  embedded?: boolean;
}

export function AddSupplyUnitPage({
  product,
  setProduct,
  nextPage,
  prevPage,
  setSupplyUnits,
  supplyUnits,
  initialProduct,
  preselectedSupplyUnits,
  embedded = false,
}: AddSupplyUnitPageProps) {
  const [initialProductLoadComplete, setInitialProductLoadComplete] = useState(false);
  const [addingSupplyUnit, setAddingSupplyUnit] = useState(false);
  const [loadingSupplyUnits, setLoadingSupplyUnits] = useState(false);
  const [errorLoading, setErrorLoading] = useState("");
  const [showDeleteInventoryModal, setShowDeleteInventoryModal] = useState(false);
  const [inventoryToDelete, setInventoryToDelete] = useState<{
    inventoryId: number | null;
    inventoryTitle: string;
    units: WizardSupplyUnit[];
  }>({ inventoryId: null, inventoryTitle: "", units: [] });
  const [partNumber, setPartNumber] = useState(product.partNumber ?? "");
  const [partNumberError, setPartNumberError] = useState("");

  useEffect(() => {
    setPartNumber(product.partNumber ?? "");
    // Embedded parents may set the Part Number themselves; validate what they set.
    if (embedded && product.partNumber) {
      setPartNumberError(validateProductPartNumber(product.partNumber));
    }
  }, [product.partNumber, embedded]);

  const allValidSupplyUnits = supplyUnits.every((unit) =>
    !partNumber || isValidChildPartNumber(partNumber, unit.part_number)
  );
  const hasSupplyUnits = supplyUnits.length > 0;

  const handlePartNumberChange = (value: string) => {
    setPartNumber(value);
    setPartNumberError(validateProductPartNumber(value));
    if (embedded) setProduct({ ...product, partNumber: value });
  };

  const handleReview = () => {
    const error = validateProductPartNumber(partNumber);
    if (error) {
      setPartNumberError(error);
      return;
    }
    setProduct({ ...product, partNumber });
    nextPage?.();
  };

  useEffect(() => {
    if (initialProductLoadComplete) return;

    const abortController = new AbortController();

    async function loadSourceProductSupplyUnits(productId: number) {
      setLoadingSupplyUnits(true);
      setErrorLoading("");
      try {
        const res = await axios.get(`/supply?productId=${productId}`, {
          signal: abortController.signal,
        });
        setSupplyUnits(res.data);
      } catch (err: any) {
        if (!abortController.signal.aborted) {
          setErrorLoading(
            err.response?.data?.message ||
              "An error occurred while loading the supply units for this product. Please close form and try again."
          );
        }
      } finally {
        if (!abortController.signal.aborted) setLoadingSupplyUnits(false);
      }
    }

    if (initialProduct && initialProduct.product_id) {
      loadSourceProductSupplyUnits(initialProduct.product_id);
      setInitialProductLoadComplete(true);
    }

    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProduct?.product_id, setSupplyUnits, setErrorLoading]);

  const openDeleteInventoryModal = (inventoryId: number, associatedSupplyUnits: WizardSupplyUnit[]) => {
    setInventoryToDelete({
      inventoryId,
      inventoryTitle: associatedSupplyUnits[0].inventory_title ?? "",
      units: associatedSupplyUnits,
    });
    setShowDeleteInventoryModal(true);
  };

  return (
    <>
      <Segment basic>
        <p style={{ color: "gray" }}>
          Add all supply units that can fulfill this product. Supply units with the same
          inventory ID will be considered equivalent inventory units upon intake.
        </p>
        {!embedded && (
          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Title</Table.HeaderCell>
                <Table.HeaderCell>Families</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell>{product.title}</Table.Cell>
                <Table.Cell>
                  {product.families.map((fam) => fam.title).join(", ")}
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
        )}
        <Form style={{ maxWidth: "400px", marginTop: "1em" }}>
          <Form.Field required>
            <label>Part Number</label>
            <Form.Input
              placeholder="Enter Part Number"
              value={partNumber}
              onChange={(_, data) => handlePartNumberChange(data.value)}
            />
            {partNumberError && (
              <Label basic color="red" pointing>
                {partNumberError}
              </Label>
            )}
          </Form.Field>
        </Form>
        <Divider section />
        <Grid>
          <Grid.Row>
            <Grid.Column floated="left" width={8}>
              <Header as="h3">Linked Supply Units</Header>
            </Grid.Column>
            <Grid.Column floated="right" width={8} textAlign="right">
              <Button primary={!addingSupplyUnit} onClick={() => setAddingSupplyUnit(!addingSupplyUnit)}>
                Add Supply Unit
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        {loadingSupplyUnits && (
          <Segment style={{ minHeight: "100px" }}>
            <Dimmer active={loadingSupplyUnits}>
              <Loader>Loading Supply Units..</Loader>
            </Dimmer>
          </Segment>
        )}
        {errorLoading && (
          <Segment basic negative>
            <Header as="h4" color="red">Error Loading Supply Units</Header>
            <p>{errorLoading}</p>
          </Segment>
        )}
        {supplyUnits.length > 0 && (
          <HierarchicalSupplyUnitsList
            supplyUnits={supplyUnits}
            openDeleteInventoryModal={openDeleteInventoryModal}
          />
        )}
        {addingSupplyUnit && (
          <AddSupplyUnitWizard
            supplyUnits={supplyUnits}
            setSupplyUnits={setSupplyUnits}
            setAddingSupplyUnit={setAddingSupplyUnit}
            product={{ ...product, partNumber }}
            preselectedSupplyUnits={preselectedSupplyUnits}
          />
        )}
      </Segment>
      {!embedded && (
        <>
          <Divider />
          <Segment basic>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <Button
                    onClick={() => {
                      setProduct({ ...product, partNumber });
                      prevPage?.();
                    }}
                    floated="left"
                    disabled={loadingSupplyUnits}
                  >
                    Back
                  </Button>
                </Grid.Column>
                <Grid.Column width={8}>
                  <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                    {!hasSupplyUnits && (
                      <Label color="red" style={{ marginRight: "10px" }} size="large">
                        Add at least one supply unit
                      </Label>
                    )}
                    {hasSupplyUnits && !allValidSupplyUnits && (
                      <Label color="red" style={{ marginRight: "10px" }} size="large">
                        One or more incompatible part numbers
                      </Label>
                    )}
                    <Button
                      color={hasSupplyUnits && allValidSupplyUnits ? "blue" : "red"}
                      onClick={handleReview}
                      disabled={loadingSupplyUnits || !hasSupplyUnits || !allValidSupplyUnits}
                    >
                      Review
                    </Button>
                  </div>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>
        </>
      )}

      <Modal
        open={showDeleteInventoryModal}
        onClose={() => setShowDeleteInventoryModal(false)}
        size="small"
      >
        <Modal.Header>
          <Header as="h3" style={{ display: "flex", alignItems: "center", gap: "0.5em", margin: 0 }}>
            <Icon name="trash" color="red" size="small" />
            Remove Inventory Record
          </Header>
        </Modal.Header>
        <Modal.Content>
          <p>
            You are about to remove the following inventory record and all its associated supply
            units from this product:
          </p>
          <Table definition compact>
            <Table.Body>
              <Table.Row>
                <Table.Cell width={4}>Inventory ID</Table.Cell>
                <Table.Cell>
                  <strong>{inventoryToDelete.inventoryId}</strong>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell>Title</Table.Cell>
                <Table.Cell>
                  <strong>{inventoryToDelete.inventoryTitle}</strong>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
          <Header as="h4" style={{ marginTop: "1.5em", marginBottom: "1em" }}>
            Associated Supply Units ({inventoryToDelete.units.length})
          </Header>
          <Table compact="very">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Supply Title</Table.HeaderCell>
                <Table.HeaderCell>UPC</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {inventoryToDelete.units.map((unit, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{unit.supply_title}</Table.Cell>
                  <Table.Cell>{unit.upc}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setShowDeleteInventoryModal(false)}>Cancel</Button>
          <Button
            negative
            icon
            labelPosition="right"
            onClick={() => {
              setSupplyUnits((prev) =>
                prev.filter((unit) => unit.inventory_id !== inventoryToDelete.inventoryId)
              );
              setShowDeleteInventoryModal(false);
            }}
          >
            Delete
            <i className="trash alternate icon" />
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}
